import {
  APIFieldAttributes,
  APIFieldType,
  APIReferenceAttributes,
  APIReferenceType,
  ArrowType,
} from '@ardoq/api-types';
import type { Edge as IEdge, Node } from 'graph/types';
import { GraphItemModel } from './GraphItem';
import { modelOperations } from '@ardoq/core';
import { referenceOperations } from '@ardoq/reference-operations';
import { DiffType } from '@ardoq/data-model';
import { toFieldTypeKey } from 'streams/fields/fields$';
import { GraphModelData } from './graphModelData';

let idCounter = 0;
export function uniqueId() {
  return `e${idCounter++}`;
}

export type RefTypeInfo = {
  referenceType: APIReferenceType;
  urlFields: APIFieldAttributes[];
};

export const createRefTypeInfoCache = ({
  referenceTypesByWorkspaceId,
  fieldsByReferenceType,
}: GraphModelData) => {
  const cache = new Map<string, RefTypeInfo>();
  return (ref: APIReferenceAttributes): RefTypeInfo => {
    const key = referenceOperations.getGlobalTypeId(ref);
    if (!cache.has(key)) {
      const referenceTypes =
        referenceTypesByWorkspaceId[ref.rootWorkspace] ?? [];
      cache.set(key, {
        referenceType: modelOperations.getModelType(
          [...referenceTypes],
          ref.type
        ),
        urlFields: (
          fieldsByReferenceType.get(toFieldTypeKey(ref.model, ref.type)) ?? []
        ).filter(field => field.type === APIFieldType.URL),
      });
    }
    return cache.get(key)!;
  };
};
interface EdgeCreationOptions {
  uniqueId: string;
  reference: APIReferenceAttributes;
  referenceType: APIReferenceType;
  filterColor: string | undefined;
  displayLabel: string;
  hasUrlField: boolean;
  sourceNode: Node;
  targetNode: Node;
  aggregatedSourceNode?: Node;
  aggregatedTargetNode?: Node;
}

type EdgeMap = {
  id: string;
  lineBeginning: ArrowType;
  lineEnding: ArrowType;
  css: string;
  displayLabel: string;
  hasUrlField: boolean;
  visualDiffType: DiffType;
};

const toEdgeMap = (options: EdgeCreationOptions): EdgeMap => ({
  id: options.uniqueId,
  lineBeginning: modelOperations.getLineBeginning(
    options.referenceType,
    options.reference.sourceCardinality
  ),
  lineEnding: modelOperations.getLineEnding(
    options.referenceType,
    options.reference.targetCardinality
  ),
  css: referenceOperations.getCssClassNames(
    options.reference,
    options.filterColor
  ),
  displayLabel: options.displayLabel,
  hasUrlField: options.hasUrlField,
  visualDiffType: referenceOperations.getVisualDiffType(options.reference),
});

/**
 * An edge can represent a reference
 * @deprecated
 */
export class Edge implements IEdge {
  edgeMap: EdgeMap;
  sourceNode: Node<GraphItemModel>;
  targetNode: Node<GraphItemModel>;
  source: string;
  target: string;
  aggregatedSource?: string;
  aggregatedTarget?: string;
  aggregatedSourceNode?: Node<GraphItemModel> | undefined;
  aggregatedTargetNode?: Node<GraphItemModel> | undefined;
  id: string;
  dataModel: APIReferenceAttributes;
  isTransparentized: boolean;

  constructor(options: EdgeCreationOptions) {
    this.edgeMap = toEdgeMap(options);
    this.id = this.edgeMap.id;
    this.dataModel = options.reference;
    this.sourceNode = options.sourceNode;
    this.targetNode = options.targetNode;
    this.aggregatedSourceNode = options.aggregatedSourceNode;
    this.aggregatedTargetNode = options.aggregatedTargetNode;
    this.source = this.sourceNode.id;
    this.target = this.targetNode.id;
    this.isTransparentized = false;

    this.aggregatedSource =
      this.aggregatedSourceNode && this.aggregatedSourceNode.id;
    this.aggregatedTarget =
      this.aggregatedTargetNode && this.aggregatedTargetNode.id;

    this.sourceNode.addOutgoingEdge(this);
    this.targetNode.addIncomingEdge(this);

    if (this.aggregatedTargetNode) {
      this.aggregatedTargetNode.addAggregatedIncomingEdge(this);
    }
    if (this.aggregatedSourceNode) {
      this.aggregatedSourceNode.addAggregatedOutgoingEdge(this);
    }
  }

  // An edge always wraps a reference (its `dataModel`).
  isComponent(): false {
    return false;
  }

  isReference(): true {
    return true;
  }

  getLineBeginning() {
    return this.edgeMap.lineBeginning;
  }

  getLineEnding() {
    return this.edgeMap.lineEnding;
  }

  getLabel() {
    return this.edgeMap.displayLabel;
  }

  getCSS() {
    return this.edgeMap.css;
  }

  hasURLFields() {
    return this.edgeMap.hasUrlField;
  }

  getVisualDiffType() {
    return this.edgeMap.visualDiffType;
  }

  destroy({ ignoreNodeUpdates = false }) {
    if (!ignoreNodeUpdates) {
      this.sourceNode.removeOutgoingEdge(this);
      this.targetNode.removeIncomingEdge(this);
    }
  }
}
